/**
 * 🔴 **轻量应用状态 —— 四项，就这四项**
 *
 * | 状态 | 为什么需要 |
 * |---|---|
 * | `session` | token + 当前账本（`X-Ledger-Id` 的来源，架构 §6.2） |
 * | `themePreference` | 🔴 **主题三态**（`system` / `light` / `dark`）—— 用户**说了什么**（`AC-N3.1`） |
 * | `systemPrefersDark` | 系统外观（**只在 `themePreference === 'system'` 时参与解析**） |
 * | `showHiddenAccounts` | 🔴 **渐进式账户的展开开关** —— 12 个起始账户里 9 个社保账户默认 `hidden`，
 *   展开与否是**纯展示决定**，服务端不参与（`hidden` 不过滤列表）。 |
 *
 * ## 🔴 为什么主题存**偏好**而不只存明/暗（本批的关键改动）
 *
 * 原来只有 `themeMode: 'light' | 'dark'` —— 那**装不下**「跟随系统」这第三种选择。
 * 若把 `'system'` 解析后就丢掉，则：① 系统从浅色切到深色时应用不会跟（不知道用户选的是"跟随"）；
 * ② 保存到服务端的会是一个"当下恰好是深色"的**快照**，用户换个时间登录就得到错的偏好。
 * ⇒ 必须**存偏好本身**，明/暗是**派生值**（`resolveThemeMode()`，`theme/theme.ts` 唯一实现）。
 *
 * ## 🔴 服务端是权威，缓存只是防闪（`AC-N3.2` / `AC-N3.3`）
 *
 * | 时点 | 主题从哪来 |
 * |---|---|
 * | 首屏（同步） | `loadCachedTheme()`（localStorage 缓存，**防 FOUC**）—— 无缓存则 `'system'` |
 * | 会话就绪后（异步） | `GET /api/settings/preferences` ⇒ `applyServerTheme()` —— **最终权威** |
 *
 * ⇒ 缓存**从不否决**服务端值；`localStorage` 也**不是**唯一载体（那正是 `AC-N3.3` 禁的）。
 *
 * ## 🔴 为什么不用 Redux / Zustand
 *
 * 四项状态、无跨页共享写入、无时间旅行需求 ⇒ `React Context + useReducer` 足够。
 * 引入状态库的收益在这里是**零**，成本是一个依赖 + 一层心智模型。
 * 何时回看：出现**超过 3 个跨页共享且相互依赖**的状态域（如离线队列 + 乐观更新 + 冲突解决）。
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  type ReactNode,
} from 'react';
import type { ThemeValue } from '@jiaying/shared';
import { clearSession, loadSession, saveSession } from '../api/session.js';
import type { Session } from '../api/http.js';
import type { ToastEvent, ToastState } from '../features/realtime-toast.js';
import { mergeToast } from '../features/realtime-toast.js';
import { nextExplicitTheme, resolveThemeMode } from '../theme/theme.js';
import { loadCachedTheme, saveCachedTheme } from '../theme/theme-cache.js';

interface AppState {
  session: Session | null;
  /** 用户**说了什么**（三态）—— 明/暗是派生值，见文件头 */
  themePreference: ThemeValue;
  /** 系统外观（`matchMedia`）—— 只在偏好为 `'system'` 时参与解析 */
  systemPrefersDark: boolean;
  showHiddenAccounts: boolean;
  /**
   * 🆕 **实时提示条**（批次 `66`）—— `null` = 不显示。
   *
   * ## 🔴 为什么它在**全局 store** 里，而不是 `useRealtime` 的局部 state
   *
   * 提示条挂在 `Shell`（顶栏之下），而**订阅**发生在 `useRealtime`（`Root` 里）——
   * 两者不是同一条组件链上的父子。放进全局 store 是**唯一不引入第二个状态载体**的接法
   * （铁律 24：同一机制只一处实现）。
   *
   * ## ⚠️ 为什么**合并**在 reducer 里（而不是 `useRealtime` 里算好再 dispatch）
   *
   * `mergeToast` 是**纯函数**（`features/realtime-toast.ts`）—— reducer 调它是"纯进纯出"，
   * 仍在 reducer 纪律内（**没有**随机数/时间/网络）。放这里的收益：**连到来的事件**（socket
   * 可能在一帧内投递多条）天然串行经过同一条 reducer，不需要在订阅回调里再攒一个 ref。
   * 🔴 但它**不在 reducer 里做时间判断**（"3 秒后自动消失"那种）—— 那是调用方的事（见下）。
   */
  realtimeToast: ToastState | null;
}

type Action =
  | { type: 'login'; session: Session }
  | { type: 'logout' }
  | { type: 'setLedger'; ledgerId: string; ledgerName: string }
  | { type: 'setTheme'; preference: ThemeValue }
  /**
   * 🆕 在当前**有效明/暗**的相反侧切一刀（顶栏按钮）。
   *
   * 🔴 取舍逻辑放在 reducer 里（纯函数、由 state 自算），**不放在 `AppProvider` 的
   * `useMemo` 内** —— 那样 `toggleTheme` 的引用会随 `state` 变化，进而被
   * `useThemeSync` 的 effect 依赖捕获（见下方 `applyServerTheme` 的同一处教训）。
   */
  | { type: 'toggleTheme' }
  | { type: 'systemScheme'; prefersDark: boolean }
  | { type: 'toggleHiddenAccounts' }
  /** 🆕 收到一条**已过闸**的实时事件（调用方必须先 `shouldToast`；本 action 不再判归属） */
  | { type: 'realtimeEvent'; event: ToastEvent }
  /** 🆕 用户点「查看」/「忽略」/ 换账本 ⇒ 清掉提示 */
  | { type: 'clearToast' };

function reducer(state: AppState, action: Action): AppState {
  switch (action.type) {
    case 'login':
      saveSession(action.session);
      // 🔴 换身份 ⇒ 清提示（上一个人的协作动态不该显示给新登录的人）
      return { ...state, session: action.session, realtimeToast: null };
    case 'logout':
      clearSession();
      // 🔴 **不清主题缓存**：清了下次登录必然闪一帧；且它不是敏感信息。
      //    换账号时的短暂不一致由 `applyServerTheme()` 在取数后纠正。
      return { ...state, session: null, realtimeToast: null };
    case 'setLedger': {
      if (state.session === null) return state;
      const next: Session = { ...state.session, ledgerId: action.ledgerId, ledgerName: action.ledgerName };
      saveSession(next);
      // 🔴 换账本 ⇒ 清提示：提示条说的是**上一本账本**的事（`shouldToast` 的账本闸只挡
      //    "晚到的事件"，挡不住"切账本前已经弹出来的那一条"⇒ 这里必须清）
      return { ...state, session: next, realtimeToast: null };
    }
    case 'realtimeEvent':
      return { ...state, realtimeToast: mergeToast(state.realtimeToast, action.event) };
    case 'clearToast':
      // 🔴 同值 ⇒ 返回**原引用**（与 `setTheme` 同理：避免无谓重渲染）
      if (state.realtimeToast === null) return state;
      return { ...state, realtimeToast: null };
    case 'setTheme': {
      // 🔴 同值 ⇒ **返回原引用**（否则 `applyServerTheme` 的 effect 会 self-trigger 成死循环）
      if (state.themePreference === action.preference) return state;
      saveCachedTheme(action.preference);
      return { ...state, themePreference: action.preference };
    }
    case 'toggleTheme': {
      /**
       * 🆕 顶栏「切换主题」—— **从当前解析结果取反，落成显式偏好**。
       *
       * 为什么"取反"要基于**解析后**的明/暗而不是 `themePreference`：
       * 用户当前可能是 `'system'`（系统深色 ⇒ 看到深色），此时点切换应当得到
       * **显式的浅色**（用户表达了"我就是要浅色"），而不是在 `system` 与 `light` 之间猜。
       * ⇒ 结果**恒为显式值**（`light` / `dark`），永不落到 `'system'`（落在 `'system'` 会让
       * 下一帧又被系统外观拉回去 = 点了等于没点）。
       *
       * ⚠️ 同值不可达（取反必然换一边），仍保留同值短路以防将来改判定。
       */
      const next: ThemeValue = nextExplicitTheme(state.themePreference, state.systemPrefersDark);
      if (state.themePreference === next) return state;
      saveCachedTheme(next);
      return { ...state, themePreference: next };
    }
    case 'systemScheme': {
      if (state.systemPrefersDark === action.prefersDark) return state;
      return { ...state, systemPrefersDark: action.prefersDark };
    }
    case 'toggleHiddenAccounts':
      return { ...state, showHiddenAccounts: !state.showHiddenAccounts };
  }
}

interface AppContextValue extends AppState {
  /** 🔴 **解析后的明/暗**（`buildTheme` 只认这个）—— 派生自 `themePreference` + 系统外观 */
  themeMode: 'light' | 'dark';
  login: (session: Session) => void;
  logout: () => void;
  setLedger: (ledgerId: string, ledgerName: string) => void;
  /** 用户**显式**选了三态中的一态（设置页 / 顶栏切换按钮） */
  setThemePreference: (preference: ThemeValue) => void;
  /**
   * 服务端下发的主题（**权威** —— `AC-N3.2`）。
   * ⚠️ 与 `setThemePreference` 走**同一个** reducer 分支：区别只在**语义**（谁说的），
   * 行为完全一致（都落缓存 + 更新偏好）—— 分成两套状态是"同一机制两个家"的开端。
   */
  applyServerTheme: (preference: ThemeValue) => void;
  /** 在**当前有效明/暗**的相反模式上切一刀，并写成**显式偏好**（顶栏按钮） */
  toggleTheme: () => void;
  toggleHiddenAccounts: () => void;
  /**
   * 🆕 收到一条**已过闸**的实时事件 ⇒ 提示条（批次 `66`）。
   *
   * 🔴 调用方**必须**先用 `shouldToast(ev, ctx)` 过闸 —— 本方法**不判归属**。
   * 为什么把闸放在调用方：判定所需的三项（账本 / 我的 userId / 可提示性）**只有订阅点拿得全**
   * （它才持有 `session` 与事件对），且那三项的判定逻辑是**唯一一份**（`features/realtime-toast.ts`）。
   * 若这里再判一遍 = 第二份 → 迟早漂移（铁律 24 的同型问题）。
   */
  pushRealtimeEvent: (event: ToastEvent) => void;
  /** 🆕 清掉当前提示（点「查看」/「忽略」/ 换账本时调用） */
  clearRealtimeToast: () => void;
}

const AppContext = createContext<AppContextValue | null>(null);

/** 当下系统是否偏好深色（无 `matchMedia` 的环境 —— node / 老浏览器 —— 一律按浅色） */
function systemPrefersDarkNow(): boolean {
  return globalThis.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
}

export function AppProvider({ children }: { children: ReactNode }): ReactNode {
  const [state, dispatch] = useReducer(reducer, undefined, () => ({
    // 🔴 会话从 localStorage **同步**恢复 —— 异步恢复会让首屏先闪一次登录页
    session: loadSession(),
    // 🔴 主题同理：先用缓存同步画出上一次的选择（防 FOUC），等取数后被服务端值覆盖
    themePreference: loadCachedTheme() ?? 'system',
    systemPrefersDark: systemPrefersDarkNow(),
    showHiddenAccounts: false,
    // 🆕 提示条初始为空（它由实时事件喂 —— 首屏不该有任何提示）
    realtimeToast: null,
  }));

  const themeMode = resolveThemeMode(state.themePreference, state.systemPrefersDark);

  /**
   * 系统外观变化 ⇒ 更新 `systemPrefersDark`。
   *
   * 🔴 **只在偏好为 `'system'` 时才有意义**，但仍**常驻监听**：否则用户从
   * `'light'` 切回 `'system'` 时拿到的会是"上次监听时的旧值"。
   * ⚠️ 首屏正确性**不依赖本 effect**（初值由 `systemPrefersDarkNow()` 同步给出）——
   * 故静态渲染（不跑 effect）下的首屏断言仍然有效。
   */
  useEffect(() => {
    const mq = globalThis.matchMedia?.('(prefers-color-scheme: dark)');
    if (mq === undefined) return undefined;
    const onChange = (e: MediaQueryListEvent): void => {
      dispatch({ type: 'systemScheme', prefersDark: e.matches });
    };
    mq.addEventListener('change', onChange);
    return () => {
      mq.removeEventListener('change', onChange);
    };
  }, []);

  /**
   * 🔴 **`applyServerTheme` 必须是稳定引用**（批次 `94` 真 bug 修）。
   *
   * 修前：它定义在上面那个 `useMemo(..., [state, themeMode])` 里 ⇒ **每次 state 变化
   * 都换一个新函数**；而 `app/useThemeSync.ts` 的 effect 依赖里含它
   * ⇒ effect 在**每一次主题切换之后**重跑，并把 query 缓存里的**旧服务端值**
   * （多数账号是 `'system'`）重新 dispatch 回来。
   *
   * 后果（负责人 2026-10-21 实测）：系统是深色 + 服务端偏好 `'system'` 时，
   * 点顶栏切换 → 状态改成 `light` → effect 立刻灌回 `'system'` → 解析回 `dark`
   * ⇒ **看起来"点了没反应、永远是暗色"**。
   *
   * ⇒ 所有对外回调一律 `useCallback`（依赖只有稳定的 `dispatch`），
   * 让"同一服务端值"只可能在**值真的变了**时被应用一次。
   */
  const login = useCallback((session: Session) => {
    dispatch({ type: 'login', session });
  }, []);
  const logout = useCallback(() => {
    dispatch({ type: 'logout' });
  }, []);
  const setLedger = useCallback((ledgerId: string, ledgerName: string) => {
    dispatch({ type: 'setLedger', ledgerId, ledgerName });
  }, []);
  const setThemePreference = useCallback((preference: ThemeValue) => {
    dispatch({ type: 'setTheme', preference });
  }, []);
  const applyServerTheme = useCallback((preference: ThemeValue) => {
    dispatch({ type: 'setTheme', preference });
  }, []);
  const toggleTheme = useCallback(() => {
    dispatch({ type: 'toggleTheme' });
  }, []);
  const toggleHiddenAccounts = useCallback(() => {
    dispatch({ type: 'toggleHiddenAccounts' });
  }, []);
  const pushRealtimeEvent = useCallback((event: ToastEvent) => {
    dispatch({ type: 'realtimeEvent', event });
  }, []);
  const clearRealtimeToast = useCallback(() => {
    dispatch({ type: 'clearToast' });
  }, []);

  const value = useMemo<AppContextValue>(
    () => ({
      ...state,
      themeMode,
      login,
      logout,
      setLedger,
      setThemePreference,
      applyServerTheme,
      toggleTheme,
      toggleHiddenAccounts,
      pushRealtimeEvent,
      clearRealtimeToast,
    }),
    [
      state,
      themeMode,
      login,
      logout,
      setLedger,
      setThemePreference,
      applyServerTheme,
      toggleTheme,
      toggleHiddenAccounts,
      pushRealtimeEvent,
      clearRealtimeToast,
    ],
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp(): AppContextValue {
  const ctx = useContext(AppContext);
  if (ctx === null) throw new Error('useApp 必须在 AppProvider 内使用');
  return ctx;
}
